<template>
    <v-app-bar app class="topbar-branded" elevation="1">
        <div class="ms-6 d-flex align-center cursor-pointer" @click="navigateTo('dashboard')">
            <v-img :src="logoFullWhite" :width="140" contain class="flex-grow-0"/>
            <span class="text-white font-weight-bold font-italic text-h5 ms-1">Go</span>
        </div>

        <!-- Circle switcher: which circle (household) am I currently viewing.
             Hidden for a solo user alone in their own circle. -->
        <v-menu v-if="authStore.user?.company && showSwitcher" location="bottom start">
            <template v-slot:activator="{ props }">
                <v-btn class="text-white text-none ms-4" variant="text" v-bind="props">
                    <v-icon start>mdi-account-multiple</v-icon>
                    <span class="text-truncate" style="max-width: 160px">{{ currentCircleName }}</span>
                    <v-icon end>mdi-menu-down</v-icon>
                </v-btn>
            </template>
            <v-list width="270" density="compact">
                <v-list-subheader>Your circles</v-list-subheader>
                <v-list-item v-for="circle in circles" :key="circle.id"
                             :active="circle.id === authStore.user.company.id"
                             @click="selectCircle(circle.id)">
                    <template #prepend>
                        <v-icon :color="circle.id === authStore.user.company.id ? 'primary' : undefined">
                            {{ circle.id === authStore.user.company.id ? 'mdi-check-circle' : 'mdi-account-multiple-outline' }}
                        </v-icon>
                    </template>
                    <v-list-item-title class="text-capitalize">{{ circle.company_name }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="canLeaveActive" prepend-icon="mdi-exit-run" base-color="error"
                             title="Leave this circle" @click="leaveCircle"/>
                <v-divider class="my-1"/>
                <v-list-item v-if="!authStore.isMinor" prepend-icon="mdi-account-multiple-plus" title="Join a circle"
                             @click="joinCircleDialog = true"/>
            </v-list>
        </v-menu>

        <v-spacer/>

        <v-btn class="me-2 text-white" icon @click="navigateTo('activity')">
            <v-icon size="large">mdi-bell</v-icon>
            <v-badge v-if="notificationsCount" color="#E6038C" offset-y="-10" overlap>
                <template #badge>{{ notificationsCount < 99 ? notificationsCount : '99+' }}</template>
            </v-badge>
        </v-btn>

        <v-menu v-if="authStore.portals?.length"
                :close-on-content-click="false"
                class="me-2"
                open-on-hover>
            <template v-slot:activator="{ props }">
                <v-btn class="text-white" icon v-bind="props"
                       @click="navigateTo('sso')">
                    <v-icon size="large">mdi-apps</v-icon>
                </v-btn>
            </template>

            <v-list>
                <v-list-item
                    v-for="portal in authStore.portals"
                    :title="portal.name"
                    @click="switchPortal(portal)"/>
            </v-list>
        </v-menu>

        <v-menu v-if="authStore.user" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
                <v-btn variant="text" class="px-1" v-bind="props">
                    <UserAvatar
                        :image="authStore.user.avatar"
                        :name="`${authStore.user.first_name ?? ''} ${authStore.user.last_name ?? ''}`"
                        :size="36"/>
                    <v-icon size="20" class="ml-1">mdi-menu-down</v-icon>
                </v-btn>
            </template>

            <v-list width="250">
                <v-list-item v-if="!showSwitcher && !authStore.isMinor" prepend-icon="mdi-account-multiple-plus"
                             title="Join a circle" @click="joinCircleDialog = true"/>
                <v-list-item title="Profile" @click="navigateTo('profile')"/>
                <v-list-item :title="`Logout (${authStore.user.first_name})`" @click="navigateTo('logout')"/>

                <!--<v-list-item class="bg-grey-lighten-4 pa-0" min-height="10">
                    <v-list-item-title class="text-capitalize text-center">
                        <small>Logged In as: <b>{{ authStore.user.company.company_name }}</b></small>
                    </v-list-item-title>
                </v-list-item>-->
            </v-list>
        </v-menu>

        <JoinCircle v-model="joinCircleDialog"/>
    </v-app-bar>
</template>

<script setup>
import {computed, ref} from "vue";
import router from "@/router";
import logoFullWhite from "@/assets/images/logo-full-white.png";
import {useAuthStore} from "@/stores/authStore";
import {useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {send, unreadNotificationsCount} from "@/composables/apiClient";
import UserAvatar from "@/components/UserAvatar.vue";
import JoinCircle from "@/components/dialogs/JoinCircle.vue";

const authStore = useAuthStore();
const {navigateTo} = useNavigation();
const {showSnackbar} = useSnackbarStore();
const notificationsCount = ref(unreadNotificationsCount);
const joinCircleDialog = ref(false);

const currentCircleName = computed(() => authStore.user?.company?.company_name ?? 'My circle');

// All circles the user belongs to. The profile endpoint returns the full set
// (including the active one); fall back to ensuring the active circle is
// present even before the profile hydrate completes.
const circles = computed(() => {
    const list = authStore.user?.companies ? [...authStore.user.companies] : [];
    const active = authStore.user?.company;
    if (active && !list.some((c) => c.id === active.id)) {
        list.unshift({id: active.id, company_name: active.company_name, owner_id: active.owner_id});
    }
    return list;
});

// Show the switcher only when circles are actually meaningful: the user
// belongs to more than one circle, or their own circle has members beyond
// them. A solo user gets "Join a circle" in the avatar menu instead.
const showSwitcher = computed(() => {
    const list = circles.value;
    if (list.length > 1) return true;
    const own = list.find((c) => c.owner_id === authStore.user?.id);
    return !!own && (own.member_count ?? 1) > 1;
});

const selectCircle = (companyId) => {
    if (companyId === authStore.user?.company?.id) return; // already viewing it
    switchCompany(companyId);
};

// Adults can leave a circle they don't own; minors must be removed by a parent.
const canLeaveActive = computed(() => {
    const c = authStore.user?.company;
    return !authStore.isMinor && !!c && c.owner_id !== authStore.user?.id;
});
const leaveCircle = () => {
    const name = authStore.user?.company?.company_name ?? 'this circle';
    if (!confirm(`Leave ${name}? You'll stop seeing its trackers.`)) return;
    send('post', 'circle/leave')
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message ?? 'You left the circle');
                router.go();
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message ?? 'Could not leave the circle', 'error'));
};

const switchCompany = (company) => {
    send('post', `switch-company/${company}`)
        .then((response) => {
            if (response.data.success) {
                // Response carries the new active company + circle list; merge it
                // so the UI reflects the switch immediately, then reload to
                // re-scope all data (devices, map, members) to the new circle.
                authStore.updateAuthUser({...authStore.user, ...response.data.data});
                router.go();
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Something went wrong', 'error')
        })
}
const switchPortal = (portal) => {
    // Open a blank page immediately to ensure the new tab is opened
    const newTab = window.open("", "_blank");

    send('post', `switch-portal/${portal.type}`)
        .then((response) => {
            if (response.data.success) {
                // Update the blank page's location to the desired URL
                newTab.location.href = `${response.data.data.url}`;
            } else {
                showSnackbar(response.data.message ?? 'Something went wrong', 'error');
                // Close the tab if the operation fails
                newTab.close();
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Something went wrong', 'error');
            // Close the tab if an error occurs
            newTab.close();
        });
}
</script>

<style scoped>
.topbar-branded {
    background: linear-gradient(135deg, #26235A 0%, #E6038C 100%) !important;
}

.topbar-branded :deep(.v-btn) {
    color: rgba(255, 255, 255, 0.85) !important;
}

.topbar-branded :deep(.v-btn:hover) {
    background: rgba(230, 3, 140, 0.15) !important;
}
</style>
